import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  CallSplitOutlined as SplitIcon,
  Close as CloseIcon,
  WarehouseOutlined as WarehouseIcon,
} from "@mui/icons-material";
import StrategyCard from "./StrategyCard";
import { LOT_STRATEGIES, WAREHOUSE_STRATEGIES, FALLBACK_OPTIONS } from "../constants";

function SectionLabel({ label, hint }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        fontSize={13}
        fontWeight={700}
        color="var(--text-primary)"
        fontFamily="Poppins, sans-serif"
        letterSpacing={0.3}
        sx={{ textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins, sans-serif">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function WarehousePriorityList({ warehouses, priority, setPriority }) {
  const addWarehouse = (wh) => {
    if (!wh || priority.find((p) => p.warehouse === wh._id)) return;
    setPriority([
      ...priority,
      { warehouse: wh._id, name: wh.name, code: wh.warehouse_code, order: priority.length + 1 },
    ]);
  };

  const remove = (id) => setPriority(priority.filter((p) => p.warehouse !== id));

  const move = (index, dir) => {
    const arr = [...priority];
    const swapWith = index + dir;
    if (swapWith < 0 || swapWith >= arr.length) return;
    [arr[index], arr[swapWith]] = [arr[swapWith], arr[index]];
    setPriority(arr.map((p, i) => ({ ...p, order: i + 1 })));
  };

  const available = warehouses.filter((wh) => !priority.find((p) => p.warehouse === wh._id));

  return (
    <Box>
      <Autocomplete
        options={available}
        getOptionLabel={(o) => `${o.name} (${o.warehouse_code})`}
        onChange={(_, val) => addWarehouse(val)}
        value={null}
        renderInput={(params) => (
          <TextField {...params} placeholder="Add warehouse to priority list" size="small" sx={{ mb: 1.5 }} />
        )}
      />
      {priority.length === 0 ? (
        <Box
          sx={{
            border: "1.5px dashed",
            borderColor: "var(--border-color)",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography fontSize={12} color="var(--text-secondary)" fontFamily="Poppins, sans-serif">
            Add warehouses above to set fulfillment priority
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {priority.map((item, i) => (
            <Box
              key={item.warehouse}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                background: "var(--bg-paper)",
                border: "1px solid var(--border-color)",
              }}
            >
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography fontSize={12} fontWeight={700} color="#fff" fontFamily="Poppins, sans-serif">
                  {i + 1}
                </Typography>
              </Box>
              <WarehouseIcon sx={{ fontSize: 16, color: "var(--text-secondary)" }} />
              <Box flex={1}>
                <Typography fontSize={13} fontWeight={600} color="var(--text-primary)" fontFamily="Poppins, sans-serif">
                  {item.name}
                </Typography>
                <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins, sans-serif">
                  {item.code}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}>
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => move(i, 1)} disabled={i === priority.length - 1}>
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => remove(item.warehouse)} color="error">
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function RuleBuilderForm({ rule, setRule, warehouses, clients, isAdmin }) {
  const field = (key) => ({
    value: rule[key] ?? "",
    onChange: (e) => setRule((r) => ({ ...r, [key]: e.target.value })),
  });

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    fontFamily: "Poppins, sans-serif",
    mb: 0.5,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Rule Identity */}
      <Box>
        <SectionLabel label="Rule Identity" />
        <Grid container spacing={2}>
          {isAdmin && (
            <Grid item xs={12}>
              <Typography sx={labelStyle}>Client *</Typography>
              <Autocomplete
                options={clients}
                getOptionLabel={(o) => o.business_name || o.name || ""}
                value={clients.find((c) => c._id === rule.client) || null}
                onChange={(_, val) => setRule((r) => ({ ...r, client: val?._id || "" }))}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select client" />}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography sx={labelStyle}>Rule Name *</Typography>
            <TextField {...field("name")} fullWidth size="small" placeholder="e.g. FIFO with Zone Priority" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={labelStyle}>Description</Typography>
            <TextField {...field("description")} fullWidth size="small" placeholder="Optional notes about this rule" />
          </Grid>
        </Grid>
      </Box>

      {/* Lot Strategy */}
      <Box>
        <SectionLabel label="Lot Selection Strategy" hint="How to pick lots within a warehouse" />
        <Grid container spacing={1.5}>
          {LOT_STRATEGIES.map((opt) => (
            <Grid item xs={12} sm={4} key={opt.value}>
              <StrategyCard
                option={opt}
                selected={rule.lot_strategy === opt.value}
                onSelect={(v) => setRule((r) => ({ ...r, lot_strategy: v }))}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Warehouse Strategy */}
      <Box>
        <SectionLabel label="Warehouse Selection Strategy" hint="How to pick which warehouse(s) to fulfill from" />
        <Grid container spacing={1.5}>
          {WAREHOUSE_STRATEGIES.map((opt) => (
            <Grid item xs={12} sm={6} key={opt.value}>
              <StrategyCard
                option={opt}
                selected={rule.warehouse_strategy === opt.value}
                onSelect={(v) => setRule((r) => ({ ...r, warehouse_strategy: v }))}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Warehouse Priority (conditional) */}
      {rule.warehouse_strategy === "PRIORITY" && (
        <Box>
          <SectionLabel label="Warehouse Priority Order" hint="Use arrows to reorder" />
          <WarehousePriorityList
            warehouses={warehouses}
            priority={rule.warehouse_priority}
            setPriority={(p) => setRule((r) => ({ ...r, warehouse_priority: p }))}
          />
        </Box>
      )}

      {/* Additional Options */}
      <Box>
        <SectionLabel label="Additional Options" />
        <Grid container spacing={2}>
          {/* Split Order */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1.5px solid var(--border-color)",
                background: "var(--bg-paper)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <SplitIcon sx={{ fontSize: 18, color: "#667eea" }} />
                <Typography fontSize={13} fontWeight={600} color="var(--text-primary)" fontFamily="Poppins, sans-serif">
                  Allow Order Splitting
                </Typography>
              </Box>
              <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins, sans-serif" mb={1}>
                Fulfill from multiple warehouses if no single warehouse has enough stock.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!rule.split_order}
                    onChange={(e) => setRule((r) => ({ ...r, split_order: e.target.checked }))}
                    size="small"
                  />
                }
                label={
                  <Typography fontSize={12} fontFamily="Poppins, sans-serif">
                    {rule.split_order ? "Enabled" : "Disabled"}
                  </Typography>
                }
              />
            </Box>
          </Grid>

          {/* Fallback */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1.5px solid var(--border-color)",
                background: "var(--bg-paper)",
              }}
            >
              <Typography fontSize={13} fontWeight={600} color="var(--text-primary)" fontFamily="Poppins, sans-serif" mb={0.5}>
                Fallback Strategy
              </Typography>
              <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins, sans-serif" mb={1}>
                If primary strategy fails, use this as fallback.
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={rule.fallback_strategy}
                onChange={(e) => setRule((r) => ({ ...r, fallback_strategy: e.target.value }))}
                SelectProps={{ native: true }}
              >
                {FALLBACK_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </TextField>
            </Box>
          </Grid>

          {/* Active status */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1.5px solid",
                borderColor: rule.is_active ? "#38c172" : "var(--border-color)",
                background: rule.is_active ? "var(--accent-bg)" : "var(--bg-paper)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography fontSize={13} fontWeight={600} color="var(--text-primary)" fontFamily="Poppins, sans-serif">
                  Rule Status
                </Typography>
                <Typography fontSize={11} color="var(--text-secondary)" fontFamily="Poppins, sans-serif">
                  Only active rules are applied during order allocation.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!rule.is_active}
                    onChange={(e) => setRule((r) => ({ ...r, is_active: e.target.checked }))}
                    color="success"
                  />
                }
                label={
                  <Chip
                    label={rule.is_active ? "Active" : "Inactive"}
                    size="small"
                    color={rule.is_active ? "success" : "default"}
                    sx={{ fontFamily: "Poppins, sans-serif", fontSize: 11 }}
                  />
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
